<template>
    <RegularModal ref="modal" modal-card-class="is-large">
        <template v-slot:title>
            <span class="icon"><i class="fas fa-chart-bar"></i></span>
            <span>{{ translate("coupons.usage_details") }} - {{ coupon?.code }}</span>
        </template>
        <template v-slot:body>
            <div v-if="loading" class="has-text-centered">
                <LoadingSpinner2 />
            </div>
            <div v-else-if="usageDetails.length === 0" class="has-text-centered">
                <Notification class="is-info">
                    <span class="icon"><i class="fas fa-info-circle"></i></span>
                    <span>{{ translate("coupons.no_usage_yet") }}</span>
                </Notification>
            </div>
            <div v-else>
                <div class="table-container">
                    <table class="table is-fullwidth is-hoverable">
                        <thead>
                            <tr>
                                <th>{{ translate("coupons.user_name") }}</th>
                                <th>{{ translate("coupons.user_email") }}</th>
                                <th>{{ translate("coupons.original_amount") }}</th>
                                <th>{{ translate("coupons.discount") }}</th>
                                <th>{{ translate("coupons.final_amount") }}</th>
                                <th>{{ translate("coupons.date_used") }}</th>
                                <th>{{ translate("common.actions") }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="usage in usageDetails" :key="usage.id">
                                <td class="clarity-mask">{{ usage.firstName }} {{ usage.lastName }}</td>
                                <td class="clarity-mask">{{ usage.email }}</td>
                                <td>{{ usage.originalAmount }}</td>
                                <td>{{ usage.discountPercentage }}%</td>
                                <td>{{ usage.finalAmount }}</td>
                                <td>{{ formatDateTime(usage.created) }}</td>
                                <td>
                                    <a
                                        :href="getRegistrationsUrl(usage.userId)"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        class="button is-small is-info"
                                    >
                                        <span class="icon is-small">
                                            <i class="fas fa-external-link-alt"></i>
                                        </span>
                                        <span>{{ translate("coupons.view_registrations") }}</span>
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </template>
        <template v-slot:footer>
            <button class="button" @click="close">{{ translate("common.close") }}</button>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import { ref } from "vue";
    import RegularModal from "../../Common/RegularModal.vue";
    import { useModal } from "../../Common/useModal";
    import { translate } from "../../Common/translations";
    import couponsApi, { Coupon, CouponUsageDetails } from "../../Api/coupons";
    import { handleError } from "../../Common/errorHandler";
    import LoadingSpinner2 from "../../Common/LoadingSpinner2.vue";
    import Notification from "../../Common/Notification.vue";
    import { convertToTimezone } from "../../Common/dateTimeUtils";
    import urls from "../../Urls/urls";

    const props = defineProps<{
        eventId: number;
        eventTimezone: string;
    }>();

    const { modal, open: openModal, close } = useModal();

    const loading = ref(false);
    const coupon = ref<Coupon | null>(null);
    const usageDetails = ref<CouponUsageDetails[]>([]);

    const open = (selectedCoupon: Coupon) => {
        coupon.value = selectedCoupon;
        usageDetails.value = [];
        loading.value = true;

        openModal();

        return couponsApi
            .getUsageDetails(props.eventId, selectedCoupon.id)
            .then(result => {
                usageDetails.value = result;
            })
            .catch(handleError)
            .finally(() => {
                loading.value = false;
            });
    };

    const formatDateTime = (dateString: string): string => {
        return convertToTimezone(dateString, props.eventTimezone, "yyyy-MM-dd HH:mm:ss");
    };

    const getRegistrationsUrl = (userId: number): string => {
        return urls.eventAdmin().registeredCompetitorsForUserPage(props.eventId, userId);
    };

    defineExpose({ open });
</script>

<style scoped></style>
